import { BsArrow90DegUp } from "react-icons/bs";
import { ReactNode } from "react";
import NextLink from "next/link";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";

// interfaces & map
import { Order, OrderItemsEntity } from "../../utils/constants/types";
import {
  OrderStatus,
  orderStatusMap,
} from "../../utils/constants/orderStatusMap";

// Utils
import formatDate from "../../utils/formatDate";
import getImportantProductFromOrder from "../../utils/orderUtils";
import {
  Badge,
  Card,
  CardContent,
  HStack,
  Skeleton,
  VStack,
  Button,
  Text,
  Grid,
  cn,
  Flex,
} from "@wootingkb/component-library";

const NO_ORDER_DATE_TEXT = msg({
  id: "Order_no_order_date_registered",
  message: "No order date registered",
});

const VIEW_THIS_ORDER_TEXT = msg({
  id: "Order_view_this_order",
  message: "View this order",
});

interface Props {
  order?: Order;
  children?: ReactNode;
}

interface MOIProps {
  order?: Order;
  isLastElement: boolean;
}

export function MergedOrderItem({ order, isLastElement }: MOIProps) {
  const { _: i18n } = useLingui();
  const mainItem = getImportantProductFromOrder(order);

  return (
    <Grid
      className={cn(
        "grid-cols-[6rem_auto] gap-4 lg:grid-cols-[8rem_auto]",
        isLastElement ? "mb-0" : "mb-4"
      )}
    >
      <Grid className="bg-surface-secondary rounded-default p-4">
        <BsArrow90DegUp fontSize="2rem" opacity="0.8" />
      </Grid>

      <VStack>
        <Text className="text-sm italic opacity-80">
          <Trans id="Order_placed_label">Placed:</Trans>{" "}
          {order && order.date_added
            ? formatDate(order.date_added)
            : i18n(NO_ORDER_DATE_TEXT)}
        </Text>
        <Text className="text-base font-bold">{order?.source_id}</Text>
        <Text className="text-base opacity-80">{mainItem?.name}</Text>
      </VStack>
    </Grid>
  );
}

export default function OrderItem({ order, children }: Props) {
  const { _: i18n } = useLingui();
  const mainItem = order?.order_items.sort(
    (a: OrderItemsEntity, b: OrderItemsEntity) => b?.price - a?.price
  )[0];

  const orderStatus = order?.status ?? OrderStatus.NewOrder;
  const status = orderStatusMap[orderStatus];
  const statusTag = i18n(status.tag);
  const isLoaded = order !== undefined;

  return (
    <NextLink
      href={`/order/view?order_nr=${encodeURIComponent(order?.source_id ?? "")}`}
    >
      <Card className="border border-transparent p-8 hover:border-outline-primary">
        <CardContent className="h-fit">
          {!isLoaded && (
            <Flex className="flex-col justify-between gap-4 md:flex-row">
              <HStack className="w-full gap-4">
                <Skeleton className="aspect-video h-20" />
                <VStack className="max-w-40 gap-2">
                  <Skeleton className="h-full" />
                  <Skeleton className="h-full" />
                  <Skeleton className="h-full" />
                </VStack>
              </HStack>
              <Flex className="order-first w-full flex-row gap-2 md:order-last md:max-w-20 md:flex-col">
                <Skeleton className="min-h-10 w-full md:h-full" />
                <Skeleton className="min-h-10 w-full md:h-full" />
              </Flex>
            </Flex>
          )}
          {isLoaded && (
            <Flex className="flex-col items-center justify-between gap-4 md:flex-row">
              <HStack className="w-full items-center gap-4">
                <img
                  src={mainItem?.image_source}
                  alt={mainItem?.name}
                  className="aspect-video h-20 rounded-default object-cover"
                />
                <VStack className="flex-1">
                  <Text size="xs">
                    <Trans id="Order_placed_label">Placed:</Trans>{" "}
                    {order && order.date_added
                      ? formatDate(order.date_added)
                      : i18n(NO_ORDER_DATE_TEXT)}
                  </Text>
                  <Text size="lg" weight="bold" className="rb-mask">
                    {order?.source_id ?? " "}
                  </Text>
                  <Text size="sm" className="max-w-lg text-balance">
                    {mainItem?.name}
                    {order && order.order_items.length > 1
                      ? ` + ${order.order_items.length - 1}`
                      : ""}
                  </Text>
                </VStack>
              </HStack>
              <Flex className="order-first h-10 w-full flex-row items-center gap-2 md:order-last md:h-full md:w-fit md:min-w-30 md:flex-col">
                <Badge
                  className="h-fit w-full whitespace-nowrap capitalize"
                  variant={status.variant}
                >
                  {statusTag}
                </Badge>
                <Button size="full" className="min-h-0 py-2">
                  {i18n(VIEW_THIS_ORDER_TEXT)}
                </Button>
              </Flex>
            </Flex>
          )}
          {children}
        </CardContent>
      </Card>
    </NextLink>
  );
}
